'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import config from '@haaretz/l-config';
import Button from '@haaretz/s-button';
import { hostname } from '@haaretz/s-consts';
import HdcLogo from '@haaretz/s-hdc-logo';
import HtzLink from '@haaretz/s-htz-link';
import HtzLogo from '@haaretz/s-htz-logo';
import Icon from '@haaretz/s-icon';
import LayoutContainer from '@haaretz/s-layout-container';
import MarkerLogo from '@haaretz/s-marker-logo';
import { useLoginApp } from '@haaretz/s-next-layout/LoginProvider';
import { usePurchasePagePersonalQuery } from '@haaretz/s-pp-queries/PurchasePage';
import getCurrentStep from '@haaretz/s-pp-routing-utils/getCurrentStep';
import getCurrentStepName from '@haaretz/s-pp-routing-utils/getCurrentStepName';
import isAbandonedCartRoute from '@haaretz/s-pp-routing-utils/isAbandonedCartRoute';
import isGqlPpStep from '@haaretz/s-pp-routing-utils/isGqlPpStep';
import isThankYouStep from '@haaretz/s-pp-routing-utils/isThankYouStep';
import useBi from '@haaretz/s-use-bi';
import usePpDataVariables from '@haaretz/s-use-pp-data-variables';
import { useRouter } from 'next/router';
import s9 from 'style9';
const promotionPageContentWidth = 368;
// `c` is short for `classNames`
const c = s9.create({
    lc: {
        "@media all and (min-width:102em)": {
            "--contentWidth": `minmax(0, ${"92rem"})`
        }
    },
    basePadding: {
        "@media all and (min-width:80em)": {
            paddingTop: "1rem",
            paddingBottom: "1rem"
        }
    },
    base: {
        alignItems: 'center',
        display: 'grid',
        gridColumnStart: 'main',
        gridTemplateColumns: '1fr auto 1fr',
        paddingBottom: "0.5rem",
        paddingTop: "0.5rem",
        width: '100%',
        minHeight: "5rem",
        "@media all and (min-width:80em)": {
            minHeight: "6.25rem"
        }
    },
    baseLogin: {
        display: 'flex',
        justifyContent: 'center'
    },
    hidden: {
        visibility: 'hidden'
    },
    backButtonGiftSubscription: {
        ':hover': {
            backgroundColor: "rgb(var(--c-primary900))",
            color: "rgb(var(--c-bgLightAllModes))"
        },
        ':focus': {
            backgroundColor: "rgb(var(--c-primary900))",
            color: "rgb(var(--c-bgLightAllModes))"
        },
        ':active': {
            backgroundColor: "rgb(var(--c-primary900))",
            color: "rgb(var(--c-bgLightAllModes))"
        }
    },
    backButton: {
        justifySelf: 'start',
        // 1px so that the icon isn't cropped by
        // the negative margin on the icon
        paddingInlineStart: '1px',
        "@media all and (max-width:47.9375em)": {
            fontSize: "var(--fz--1)",
            lineHeight: "var(--lh--1)",
            paddingInlineStart: "0.25rem"
        }
    },
    logoContainer: {
        textAlign: 'center'
    },
    logo: {
        fontSize: "1.75rem",
        "@media all and (min-width:80em)": {
            fontSize: "2.25rem"
        }
    },
    whiteText: {
        color: "rgb(var(--c-bgLightAllModes))"
    },
    tmLogo: {
        color: "rgb(var(--c-primary800))"
    },
    tmLogoGroupSub: {
        color: "rgb(var(--c-neutral1200))"
    },
    hdcLogo: {
        fontSize: "2rem",
        color: "rgb(var(--c-neutral1200))",
        "@media all and (min-width:48em) and (max-width:79.9375em)": {
            fontSize: "2.25rem"
        },
        "@media all and (min-width:80em)": {
            fontSize: "2.75rem"
        }
    },
    safePurchaseWidth: {
        paddingTop: "0.25rem",
        width: "4rem",
        "@media all and (min-width:80em) and (max-width:101.9375em)": {
            width: "4.5rem"
        },
        "@media all and (min-width:102em)": {
            width: "4.5rem"
        }
    },
    backButtonIcon: {
        transform: 'initial'
    },
    safePurchase: {
        alignItems: 'center',
        aspectRatio: '1',
        borderRadius: "clamp(0px, ((100vw - 4px) - 100%) * 9999, 50%) / 50%",
        backgroundColor: "rgb(var(--c-ppSafePurchaseBg))",
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        justifySelf: 'end',
        textAlign: 'center',
        borderBottomColor: "rgba(var(--c-quaternary700),0.3)",
        borderBottomWidth: "2px",
        borderBottomStyle: 'solid',
        paddingBottom: "calc(0.25rem - 2px)",
        borderInlineEndColor: "rgba(var(--c-quaternary700),0.3)",
        borderInlineEndWidth: "2px",
        borderInlineEndStyle: 'solid',
        paddingInlineEnd: "0.25rem",
        borderInlineStartColor: "rgba(var(--c-quaternary700),0.3)",
        borderInlineStartWidth: "2px",
        borderInlineStartStyle: 'solid',
        paddingInlineStart: "0.25rem",
        borderTopColor: "rgba(var(--c-quaternary700),0.3)",
        borderTopWidth: "2px",
        borderTopStyle: 'solid',
        paddingTop: "calc(0.25rem - 2px)"
    },
    safePurchaseGiftSubscription: {
        backgroundColor: "rgb(var(--c-bgLightAllModes))"
    },
    lockIcon: {
        fontSize: "1.5rem",
        "@media all and (min-width:80em)": {
            fontSize: "2rem"
        }
    },
    safePurchaseText: {
        fontWeight: 500,
        fontFamily: 'var(--font-brand),"Helvetica Neue",Helvetica,arial,tahoma,sans-serif',
        color: "rgb(var(--c-quaternary700))",
        fontSize: "var(--fz--4)",
        lineHeight: "0.75rem",
        "@media all and (min-width:80em) and (max-width:101.9375em)": {
            fontSize: "var(--fz--4)",
            lineHeight: "0.75rem"
        },
        "@media all and (min-width:102em)": {
            fontSize: "var(--fz--5)",
            lineHeight: "0.75rem"
        }
    }
});
const cleanReload = ()=>{
    const reloadLocation = window.location.origin || `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`;
    window.location.assign(reloadLocation);
};
function usePpMasthead() {
    const biAction = useBi();
    const router = useRouter();
    const currentStepName = getCurrentStepName(router.pathname);
    const variables = usePpDataVariables();
    const { data } = usePurchasePagePersonalQuery(variables, {
        enabled: false
    });
    const pageData = data?.PurchasePage?.pageData;
    const buttonShouldReload = isAbandonedCartRoute(router.query);
    const hideBackButton = isThankYouStep(currentStepName) || currentStepName === 'offers';
    const isGiftSubscriptionPage = currentStepName === 'giftSubscription';
    const hasSafePurchase = currentStepName !== 'groupSubscription' && currentStepName !== 'giftSubscription';
    const getBiData = ()=>{
        const currentStep = pageData && isGqlPpStep(currentStepName) && getCurrentStep(currentStepName, pageData.allSteps).currentStepIndex + 1;
        const additionalInfoBiData = {
            additional_info: {
                ...currentStep ? {
                    stage_number: currentStep.toString()
                } : {}
            }
        };
        const biData = {
            stage: currentStepName,
            ...additionalInfoBiData
        };
        return biData;
    };
    function BackToPpButton() {
        const backButtonClick = ()=>{
            biAction({
                action_id: 169,
                feature: 'Back on promotions',
                feature_type: 'Marketing',
                ...getBiData()
            });
            if (buttonShouldReload) cleanReload();
            else router.back();
        };
        return /*#__PURE__*/ _jsx(Button, {
            onClick: backButtonClick,
            variant: "neutral",
            priority: "tertiary",
            startIcon: "chevron",
            styleExtend: [
                c.backButton,
                hideBackButton && c.hidden,
                ...isGiftSubscriptionPage ? [
                    c.backButtonGiftSubscription,
                    c.whiteText
                ] : []
            ],
            startIconStyleExtend: [
                c.backButtonIcon
            ],
            "data-testid": "lite-masthead-back-button",
            children: 'חזרה'
        });
    }
    return {
        getBiData,
        BackToPpButton,
        isGiftSubscriptionPage,
        hasSafePurchase,
        currentStepName
    };
}
function useLoginMasthead() {
    const { loweredCaseSpacedStage } = useLoginApp();
    const getBiData = ()=>{
        return {
            stage: loweredCaseSpacedStage
        };
    };
    return {
        getBiData,
        BackToPpButton: ()=>null,
        isGiftSubscriptionPage: false,
        hasSafePurchase: false,
        currentStepName: null
    };
}
export default function LiteMasthead() {
    const biAction = useBi();
    const appType = config.get('appType');
    // eslint-disable-next-line react-compiler/react-compiler
    const usePropsByApp = appType === 'promotions-page' ? usePpMasthead : useLoginMasthead;
    const { hasSafePurchase, BackToPpButton, currentStepName, getBiData, isGiftSubscriptionPage } = // eslint-disable-next-line react-compiler/react-compiler
    usePropsByApp();
    const logoOnClick = ()=>{
        biAction({
            action_id: 80,
            feature: 'Promotions footer',
            feature_type: 'Content',
            ...getBiData()
        });
    };
    return /*#__PURE__*/ _jsx(LayoutContainer, {
        as: "header",
        styleExtend: [
            c.lc
        ],
        children: /*#__PURE__*/ _jsxs("div", {
            className: s9(c.base, c.basePadding, appType === 'login' && c.baseLogin),
            children: [
                /*#__PURE__*/ _jsx(BackToPpButton, {}),
                /*#__PURE__*/ _jsx("div", {
                    className: s9(c.logoContainer),
                    children: /*#__PURE__*/ _jsx(HtzLink, {
                        href: `https://www.${hostname}`,
                        onClick: logoOnClick,
                        "data-testid": "lite-masthead-logo-link",
                        children: /*#__PURE__*/ _jsx(HtzLogo, {
                            styleExtend: [
                                c.logo,
                                isGiftSubscriptionPage && c.whiteText
                            ]
                        })
                    })
                }),
                hasSafePurchase && /*#__PURE__*/ _jsxs("div", {
                    className: s9(c.safePurchase, c.safePurchaseWidth, isGiftSubscriptionPage && c.safePurchaseGiftSubscription),
                    children: [
                        /*#__PURE__*/ _jsx(Icon, {
                            icon: "lock",
                            variant: "success",
                            styleExtend: [
                                c.lockIcon
                            ]
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: s9(c.safePurchaseText),
                            children: 'תשלום מאובטח'
                        })
                    ]
                })
            ]
        })
    });
}
const colorCssVariablesHtz = [
    "primary900",
    "bgLightAllModes",
    "primary800",
    "neutral1200",
    "quaternary700",
    "ppSafePurchaseBg"
];
const lineHeightCssVariablesHtz = [
    "--lh--1"
];
const fontSizeCssVariablesHtz = [
    "--fz--1",
    "--fz--4",
    "--fz--5"
];

//# sourceMappingURL=LiteMasthead.js.map